import { useMemo, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { isEmpty } from '../../utils'
import { BOOK_BASE } from '../../utils/constants'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { colorThemeOptions, colorThemeOptionsWithUserCustomizations } from './Options'

const render = ({ base }: PocketComponentProps<any>) => {
    const navigate = usePSNavigate()
    const [user, setUser] = useState(base.me.clone())
    const [showCopySuccess, setShowCopySuccess] = useState(false)
    const [showEmailSuccess, setShowEmailSuccess] = useState(false)

    const paymentLink = useMemo(
        () => `${BOOK_BASE}/cash/${user.username ?? user.key ?? ''}`,
        [user]
    )

    const colorItems = useMemo(() => {
        const colorOptions = colorThemeOptionsWithUserCustomizations(user)
        return colorOptions.map((option) => ({
            id: option.value,
            title: option.label,
            tertiaryView: (
                <div className="flex items-center gap-x-2">
                    <div
                        className="size-5 rounded-full"
                        style={{ backgroundColor: option.value }}
                    ></div>
                </div>
            ),
        }))
    }, [user])

    const hasEdits =
        base.me.primaryColor !== user.primaryColor || base.me.secondaryColor !== user.secondaryColor

    const handleSave = async () => {
        base.showProgressBar('Saving...')
        const result = await RecordFactory.getInstance().sendUser(user)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }
        navigate('/settings')
    }

    const handleSaveAndPreview = async () => {
        await handleSave()
        window.open(paymentLink, '_blank')
    }

    return (
        <WorkflowPage
            title="Direct Payments"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="secondary" onClick={handleSaveAndPreview}>
                        Save & Preview
                    </Button>
                    <Button intent="primary" onClick={handleSave} disabled={!hasEdits}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start">
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection
                        title="Direct payments link"
                        icon="dollar.large"
                        thirdLine={
                            <div className="mt-2 flex w-full items-center justify-between rounded-lg bg-gray-100 p-[5px] pl-4">
                                <span className="text-base font-normal">{paymentLink}</span>
                                <Button
                                    intent="secondary"
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard
                                            .writeText(paymentLink)
                                            .then(() => setShowCopySuccess(true))
                                    }}
                                >
                                    Copy
                                </Button>
                            </div>
                        }
                    />

                    <WorkflowPage.IconSection
                        title="Primary color"
                        icon="palette.large"
                        description="Choose your widget colors here or send us your hex codes for more customization."
                    >
                        <SearchableSelect
                            size="medium"
                            className="w-full border-gray-500"
                            items={colorItems}
                            onSelectItem={(item) =>
                                setUser(user.clone({ primary_color: item?.id }))
                            }
                            selectedItemID={user.primaryColor}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Secondary color" icon="palette.large">
                        <SearchableSelect
                            size="medium"
                            className="w-full border-gray-500"
                            items={colorItems}
                            onSelectItem={(item) =>
                                setUser(user.clone({ secondary_color: item?.id }))
                            }
                            selectedItemID={user.secondaryColor}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <Modal
                isOpen={showCopySuccess}
                title="Direct payments link copied!"
                onClose={() => setShowCopySuccess(false)}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="primary" onClick={() => setShowCopySuccess(false)}>
                            Close
                        </Button>
                    </div>
                }
            >
                Your Direct payments link has been copied to your clipboard! Paste it to share with
                a client or on social media.
            </Modal>

            <Modal
                isOpen={showEmailSuccess}
                title="Success!"
                onClose={() => setShowEmailSuccess(false)}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="primary" onClick={() => setShowEmailSuccess(false)}>
                            Close
                        </Button>
                    </div>
                }
            >
                <p>Setup instructions sent to {user.email || 'your email'}.</p>
            </Modal>

            <NavigationBlockerPrompt when={hasEdits} />
        </WorkflowPage>
    )
}

export const SetupOnlinePaymentPage = usePSComponent(render)
